import React, { useCallback } from 'react';
import {
  Modal,
  Tabs,
  TabList,
  Tab,
  Margin,
  SecondaryCTAButton,
  TabPanel,
} from '@ovotech/element-v4';
import { ModalStepRenderer } from '../ModalStepRenderer';
import { WhatYouGetStep1 } from '@/src/pages/AnytimeEligibilityV2/pages/PreEligibilityProducts/steps/WhatYouGetStep1';
import { WhatYouGetStep2 } from '@/src/pages/AnytimeEligibilityV2/pages/PreEligibilityProducts/steps/WhatYouGetStep2';
import { WhatYouGetStep3 } from '@/src/pages/AnytimeEligibilityV2/pages/PreEligibilityProducts/steps/WhatYouGetStep3';
import { WhatYouGetStep4 } from '@/src/pages/AnytimeEligibilityV2/pages/PreEligibilityProducts/steps/WhatYouGetStep4';
import { NeedToKnowStep1 } from '@/src/pages/AnytimeEligibilityV2/pages/PreEligibilityProducts/steps/NeedToKnowStep1';
import { NeedToKnowStep2 } from '@/src/pages/AnytimeEligibilityV2/pages/PreEligibilityProducts/steps/NeedToKnowStep2';
import { breakpoints } from '@/src/utils/mediaQuery';
import { Media } from '@/src/components/Media';
import styled from 'styled-components';
import { Divider } from '@/src/pages/AnytimeEligibilityV2/pages/PreEligibilityProducts/PreEligibilityProducts.styled';

export type ModalTab = 'what-you-get' | 'need-to-know';

const STEP_COMPONENTS = {
  'what-you-get': [
    WhatYouGetStep1,
    WhatYouGetStep2,
    WhatYouGetStep3,
    WhatYouGetStep4,
  ],
  'need-to-know': [NeedToKnowStep1, NeedToKnowStep2],
};

interface MonthlyPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentTab: ModalTab;
  currentStep: number;
  onTabSwitch: (tab: ModalTab) => void;
  onNextStep: () => void;
  onPrevStep: () => void;
}

const StyledTabPanel = styled(TabPanel)`
  padding: 0;
`;

export const MonthlyPlanModal: React.FC<MonthlyPlanModalProps> = ({
  isOpen,
  onClose,
  currentTab,
  currentStep,
  onTabSwitch,
  onNextStep,
  onPrevStep,
}) => {
  const handleTabSwitch = useCallback(
    (activeTabNumber: number) => {
      let selectedTab: ModalTab = 'what-you-get';
      if (activeTabNumber === 1) {
        selectedTab = 'need-to-know';
      }
      onTabSwitch(selectedTab);
    },
    [onTabSwitch],
  );

  return (
    <Modal title="" isOpen={isOpen} onClose={onClose}>
      <Tabs onSelected={handleTabSwitch} fullWidth>
        <TabList>
          <Tab active={currentTab === 'what-you-get'}>What you'll get</Tab>
          <Tab active={currentTab === 'need-to-know'}>Need to know</Tab>
        </TabList>
        <StyledTabPanel>
          <Media query={breakpoints.mediumUp}>
            <ModalStepRenderer
              steps={STEP_COMPONENTS['what-you-get']}
              onClose={onClose}
              currentTab={currentTab}
              currentStep={currentStep}
              onNextStep={onNextStep}
              onPrevStep={onPrevStep}
            />
          </Media>
          <Media query={breakpoints.mediumDown}>
            {STEP_COMPONENTS['what-you-get'].map((StepComponent, index) => (
              <StepComponent key={index} />
            ))}
            <Margin top={4} />
            <Divider />
            <Margin top={4}>
              <SecondaryCTAButton onClick={onClose}>Close</SecondaryCTAButton>
            </Margin>
          </Media>
        </StyledTabPanel>
        <StyledTabPanel>
          <Media query={breakpoints.mediumUp}>
            <ModalStepRenderer
              steps={STEP_COMPONENTS['need-to-know']}
              onClose={onClose}
              currentTab={currentTab}
              currentStep={currentStep}
              onNextStep={onNextStep}
              onPrevStep={onPrevStep}
            />
          </Media>
          <Media query={breakpoints.mediumDown}>
            {STEP_COMPONENTS['need-to-know'].map((StepComponent, index) => (
              <StepComponent key={index} />
            ))}
            <Margin top={4} />
            <Divider />
            <Margin top={4}>
              <SecondaryCTAButton onClick={onClose}>Close</SecondaryCTAButton>
            </Margin>
          </Media>
        </StyledTabPanel>
      </Tabs>
    </Modal>
  );
};
